import React from "react";
import { IKO, IKO_KEYS } from "@/lib/design";

// WW-157 · one line, same pattern as the published page:
// work lists as tabs with counts | group menus (poster, missing info, timing, source, iko) | sort.
const PLATFORMS = ["unknown", "instagram", "facebook", "x", "tiktok", "linkedin", "other"];
const GATES = [
  ["ready", "passes all"], ["has_iko", "missing iko"], ["has_start", "missing start"],
  ["has_venue", "missing venue"], ["has_price", "missing price"], ["has_cover", "missing cover"],
  ["has_description", "short description"], ["has_organiser", "missing curator"],
  ["has_ticket_link_if_paid", "no ticket link (paid)"],
];
const SOURCE_LABELS = { poster_drop: "poster drop", jsonld: "event sites", self_submit: "submitted by a member", manual: "manual · joy (legacy)", valley_scout: "valley scout" };
const sourceLabel = (s) => SOURCE_LABELS[s] || s.replace(/_/g, " ").toLowerCase();
const n = (v) => (typeof v === "number" ? v : "…");
const menuCls = "shrink-0 rounded-full border bg-white px-2 py-0.5 text-[12px] lowercase";
const menuStyle = { borderColor: "#8A8171", color: "#1A1A30" };

const Tab = ({ active, onClick, testId, children, title }) => (
  <button type="button" data-testid={testId} onClick={onClick} title={title}
    className={`shrink-0 whitespace-nowrap rounded-full border px-3 py-1 text-[11px] font-bold lowercase tracking-wide transition-colors ${
      active ? "bg-neutral-900 text-white border-neutral-900" : "bg-white text-neutral-900 border-neutral-900 hover:bg-neutral-50"}`}>
    {children}
  </button>
);

const Divider = () => <span className="shrink-0 px-1 text-neutral-400">|</span>;

export const QueueFilterRow = ({ f, hidden }) => {
  const tab = f.needsCrop ? "crop" : f.posterNotChecked ? "poster" : f.yearWrong ? "year" : "all";
  const setTab = (k) => {
    f.setNeedsCrop(k === "crop");
    f.setPosterNotChecked(k === "poster");
    f.setYearWrong(k === "year");
  };
  const pc = f.posterStateCounts || {};
  const nPoster = f.posterStateCounts ? (pc.no_poster || 0) + (pc.needs_work || 0) + (pc.not_checked || 0) : null;
  const posterValue = f.overlay ? "overlay" : f.platform !== "all" ? `platform:${f.platform}` : "any";
  const setPoster = (v) => {
    f.setOverlay(v === "overlay");
    f.setPlatform(v.startsWith("platform:") ? v.slice(9) : "all");
  };
  const sourceValue = f.source !== "all" ? `source:${f.source}`
    : f.capturedBy !== "all" ? `captured:${f.capturedBy}`
    : f.dup !== "all" ? `dup:${f.dup}` : "any";
  const setSourceGroup = (v) => {
    const [kind, ...rest] = v.split(":");
    const val = rest.join(":");
    f.setSource(kind === "source" ? val : "all");
    f.setCapturedBy(kind === "captured" ? val : "all");
    f.setDup(kind === "dup" ? val : "all");
  };
  return (
    <div data-testid="review-filter-row" className="flex min-h-[52px] shrink-0 flex-wrap items-center gap-2 border-b px-6 py-2"
         style={{ borderColor: "#E4D7C0", display: hidden ? "none" : undefined }}>
      <Tab testId="queue-filter-all" active={tab === "all"} onClick={() => setTab("all")}>
        all <span data-testid="queue-all-count">{n(f.total)}</span>
      </Tab>
      <Tab testId="queue-filter-needs-crop" active={tab === "crop"} onClick={() => setTab("crop")}>
        needs crop <span data-testid="queue-needs-crop-count">{n(f.needsCropLabel)}</span>
      </Tab>
      <Tab testId="queue-filter-poster-not-checked" active={tab === "poster"} onClick={() => setTab("poster")}
           title={`needs a poster ${pc.no_poster || 0} · needs work ${pc.needs_work || 0} · not checked ${pc.not_checked || 0}`}>
        poster not checked <span data-testid="queue-poster-not-checked-count">{n(nPoster)}</span>
      </Tab>
      <Tab testId="queue-filter-year-wrong" active={tab === "year"} onClick={() => setTab("year")}
           title="rows the extractor may have year misread · more than 60 days before today">
        year looks wrong <span data-testid="queue-year-wrong-count">{n(f.yearWrongCount)}</span>
      </Tab>
      <Divider />
      <select data-testid="queue-filter-poster" value={posterValue} onChange={(e) => setPoster(e.target.value)} className={menuCls} style={menuStyle}>
        <option value="any">poster · any</option>
        <option value="overlay">overlay on poster · {n(f.total === null ? null : f.overlayCount)}</option>
        {PLATFORMS.map((p) => <option key={p} value={`platform:${p}`}>screenshot from {p}</option>)}
      </select>
      <select data-testid="queue-filter-gate" value={f.gate} onChange={(e) => f.setGate(e.target.value)} className={menuCls} style={menuStyle}>
        <option value="all">missing info · any</option>
        {GATES.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
      </select>
      <select data-testid="queue-filter-timing" value={f.pastEvents ? "past" : "upcoming"} onChange={(e) => f.setPastEvents(e.target.value === "past")} className={menuCls} style={menuStyle}>
        <option value="upcoming">timing · upcoming and on now</option>
        <option value="past">timing · past events</option>
      </select>
      <select data-testid="queue-filter-source" value={sourceValue} onChange={(e) => setSourceGroup(e.target.value)} className={menuCls} style={menuStyle}>
        <option value="any">source · any</option>
        {f.sourceKeys.map((s) => <option key={s} value={`source:${s}`}>{sourceLabel(s)}</option>)}
        <option value="dup:dup">flagged duplicate</option>
        <option value="dup:unique">unique only</option>
      </select>
      <select data-testid="queue-filter-iko" value={f.iko} onChange={(e) => f.setIko(e.target.value)} className={menuCls} style={menuStyle}>
        <option value="all">iko · any</option>
        {IKO_KEYS.map((k) => <option key={k} value={k}>{IKO[k].label.toLowerCase()}</option>)}
      </select>
      <Divider />
      <select data-testid="queue-sort" value={f.sortKey} onChange={(e) => f.setSortKey(e.target.value)} className={menuCls} style={menuStyle}>
        <option value="start">soonest event first</option>
        <option value="newest">newest ingested</option>
        <option value="oldest">oldest ingested</option>
      </select>
      {f.loading && <span data-testid="queue-list-loading" className="shrink-0 text-[11px] lowercase text-neutral-500">loading...</span>}
    </div>
  );
};
